import { Component, ReactNode } from 'react';
import { RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  isChunkError: boolean;
}

export class LazyLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's a chunk loading error
    const isChunkError = 
      error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('error loading dynamically imported module');
    
    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('LazyLoadErrorBoundary caught an error:', error, errorInfo);
    
    // Auto-reload on chunk errors — the user should never see this screen
    if (this.state.isChunkError) {
      const reloadKey = 'chunk-error-reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      
      // Prevent infinite reload loops: only auto-reload once per 30s
      if (!lastReload || now - parseInt(lastReload, 10) > 30_000) {
        sessionStorage.setItem(reloadKey, now.toString());
        console.log('[LazyLoadErrorBoundary] Auto-reloading to recover from chunk error.');
        window.location.reload();
        return;
      }
      console.warn('[LazyLoadErrorBoundary] Skipping auto-reload to prevent loop.');
    }
  }

  handleRetry = () => {
    // Clear error state and reload the page to fetch fresh chunks
    this.setState({ hasError: false, error: undefined, isChunkError: false });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <RefreshCw className="h-6 w-6 text-muted-foreground" />
              </div>
              <CardTitle>
                {this.state.isChunkError 
                  ? 'Page Loading Failed' 
                  : 'Something went wrong'}
              </CardTitle>
              <CardDescription>
                {this.state.isChunkError 
                  ? 'The page could not be loaded. This might be due to a network issue or a recent update.'
                  : 'An unexpected error occurred while loading this page.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={this.handleRetry} 
                className="w-full gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
              <Button 
                onClick={this.handleGoHome} 
                variant="outline" 
                className="w-full gap-2"
              >
                <Home className="h-4 w-4" />
                Return to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
